import { useTranslation } from 'react-i18next';
import DocumentHead from '@/components/DocumentHead';

/**
 * Copyright / DMCA notice — the public posting required by 17 U.S.C. §512(c)(2).
 *
 * Designating an agent with the Copyright Office is only half the safe harbour:
 * the agent's contact details must ALSO be available "through its service,
 * including on its website in a location accessible to the public". This route
 * is that posting, and it is deliberately a code route rather than a
 * `/page/:slug` DB page — tenant-editable content can be edited away or never
 * created, and a missing posting is what actually costs the safe harbour.
 *
 * It renders on every tenant site the platform serves. That is intentional and
 * accurate: Rumio LLC is the service provider hosting all of them, including
 * sites operated by third-party tenants. The copy says "hosted by" rather than
 * "operated by" so it stays true in both cases.
 *
 * The agent block below is intentionally NOT translated — it is a legal name,
 * a postal address, and a registration number filed in English with a U.S.
 * federal agency. Translating any of it would misstate the filing.
 */

/** Registration on file with the U.S. Copyright Office DMCA agent directory. */
const DMCA_REGISTRATION = 'DMCA-1077802';
const AGENT_EMAIL = 'dmca@rumio.world';

export default function Copyright() {
  const { t } = useTranslation('legal');

  return (
    <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8" data-testid="copyright-page">
      <DocumentHead
        title={t('dmca.title', { defaultValue: 'Copyright & DMCA' })}
        description={t('dmca.metaDescription', {
          defaultValue:
            'How to report copyright infringement, and the designated agent for receiving notices.',
        })}
        canonicalUrl="/copyright"
      />

      <h1 className="font-display text-3xl font-bold text-ink">
        {t('dmca.heading', { defaultValue: 'Copyright & DMCA' })}
      </h1>
      <p className="mt-3 text-ink-soft">
        {t('dmca.intro', {
          defaultValue:
            'How to report content you own that appears on this site.',
        })}
      </p>

      <section className="mt-8 space-y-4 text-sm text-ink-soft">
        <p>
          {t('dmca.designation', {
            registration: DMCA_REGISTRATION,
            defaultValue:
              'This site is hosted by Rumio LLC, which has designated an agent with the U.S. Copyright Office to receive notices of claimed copyright infringement under 17 U.S.C. §512 (registration number {{registration}}). If you believe material on this service infringes a copyright you own or represent, send a written notice to the designated agent:',
          })}
        </p>

        {/* i18n-exempt: legal name, postal address and registration number as filed */}
        <address
          className="not-italic rounded-lg border border-line bg-surface-muted p-4 leading-relaxed text-ink"
          data-testid="copyright-dmca-agent"
        >
          Stephen Bittner, Designated Agent
          <br />
          Rumio LLC
          <br />
          4501 Woodward Ave Ste 101-B, PMB 178
          <br />
          Detroit, MI 48201, USA
          <br />
          <a
            href={`mailto:${AGENT_EMAIL}`}
            className="underline underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded"
          >
            {AGENT_EMAIL}
          </a>
          <br />
          313-488-3256
        </address>

        <h2 className="pt-2 text-base font-semibold text-ink">
          {t('dmca.requirementsHeading', { defaultValue: 'What your notice must include' })}
        </h2>
        <p>
          {t('dmca.requirements', {
            defaultValue:
              'To be effective, your notice must include: your physical or electronic signature; identification of the copyrighted work you claim was infringed; identification of the material you say is infringing, with enough detail for us to find it; your address, phone number, and email; a statement that you believe in good faith the use is not authorized by the copyright owner, its agent, or the law; and a statement, under penalty of perjury, that the information in your notice is accurate and that you are the owner or authorized to act on the owner’s behalf.',
          })}
        </p>
        <p>
          {t('dmca.misrepresentation', {
            defaultValue:
              'Please send notices only about material you actually own or are authorized to represent. Knowingly misrepresenting that material is infringing carries liability for damages under 17 U.S.C. §512(f).',
          })}
        </p>

        <h2 className="pt-2 text-base font-semibold text-ink">
          {t('dmca.counterHeading', { defaultValue: 'Counter-notification and repeat infringers' })}
        </h2>
        <p>
          {t('dmca.counter', {
            defaultValue:
              'If we remove something of yours and you believe that was a mistake or a misidentification, you may send a counter-notification to the same address. We terminate the accounts of repeat infringers.',
          })}
        </p>
      </section>
    </main>
  );
}
